import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronsUpDown, Check, type LucideIcon } from 'lucide-react';
import baseapi from '../services/baseapi';

interface DropdownItem {
    id?: string | number | null;
    [key: string]: any;
}

interface SingleSelectLocalSearchApiDropdownProps {
    label?: string;
    value?: DropdownItem | null;
    onChange: (item: DropdownItem | null) => void;
    disabled?: boolean;
    placeholder?: string;
    className?: string;
    apiEndpoint: string;
    icon: LucideIcon;
    searchPlaceholder?: string;
    searchInputPlaceholder: string;
    error?: string;
    required?: boolean;
    displayProperty: string;
    secondaryDisplayProperty?: string;
    searchFields: string[];
    additionalQueries?: Record<string, any>;
    additionalParams?: Record<string, any>;
    getDisplayText?: (item: DropdownItem) => string | React.ReactNode;
    getSecondaryDisplayText?: (item: DropdownItem) => string | React.ReactNode;
    idProperty?: string;
    buttonClassName?: string;
}


const SingleSelectLocalSearchApiDropdown = React.memo<SingleSelectLocalSearchApiDropdownProps>(({
    label,
    value,
    onChange,
    disabled = false,
    placeholder = "Select item",
    className = "",
    apiEndpoint,
    icon: Icon,
    searchInputPlaceholder,
    error,
    required,
    displayProperty,
    secondaryDisplayProperty,
    searchFields,
    additionalQueries = {},
    additionalParams = {},
    getDisplayText,
    getSecondaryDisplayText,
    idProperty = 'id',
    buttonClassName = ""
}) => {

    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
    const [allItems, setAllItems] = useState<DropdownItem[]>([]);
    const [filteredItems, setFilteredItems] = useState<DropdownItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Fetch data once on mount or when apiEndpoint changes
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await baseapi.get(apiEndpoint, {
                params: {
                    ...additionalQueries,
                    ...additionalParams
                }
            });

            if (response.data.status) {
                const data = response.data.data || [];
                setAllItems(data);
                setFilteredItems(data);
                setHasLoaded(true);
            }
        } catch (error) {
            console.error(`Error fetching data from ${apiEndpoint}:`, error);
        } finally {
            setIsLoading(false);
        }
    }, [apiEndpoint, JSON.stringify(additionalQueries), JSON.stringify(additionalParams)]);

    useEffect(() => {
        setHasLoaded(false);
        setAllItems([]);
        setFilteredItems([]);
    }, [apiEndpoint, JSON.stringify(additionalQueries), JSON.stringify(additionalParams)]);

    useEffect(() => {
        if (isOpen && !hasLoaded) {
            fetchData();
        }
    }, [isOpen, hasLoaded, fetchData]);

    // Handle local searching
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredItems(allItems);
            return;
        }

        const lowerTerm = searchTerm.toLowerCase();
        const filtered = allItems.filter(item => {
            return searchFields.some(field => {
                const val = item[field];
                return val && String(val).toLowerCase().includes(lowerTerm);
            });
        });
        setFilteredItems(filtered);
    }, [searchTerm, allItems, searchFields]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                dropdownRef.current && !dropdownRef.current.contains(target) &&
                menuRef.current && !menuRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const calculateDropdownPosition = useCallback(() => {
        if (!buttonRef.current) return;
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 320;
        const windowHeight = window.innerHeight;
        const spaceBelow = windowHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let position: 'bottom' | 'top' = 'bottom';
        let style: React.CSSProperties = {
            width: `${buttonRect.width}px`,
            left: `${buttonRect.left}px`,
            position: 'fixed'
        };

        if (spaceBelow < dropdownHeight + 20 && spaceAbove > dropdownHeight + 20) {
            position = 'top';
            style.bottom = `${windowHeight - buttonRect.top + 8}px`;
        } else {
            position = 'bottom';
            style.top = `${buttonRect.bottom + 8}px`;
        }

        setDropdownPosition(position);
        setMenuStyle(style);
    }, []);

    const updatePosition = useCallback(() => {
        if (isOpen) {
            calculateDropdownPosition();
        }
    }, [isOpen, calculateDropdownPosition]);

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);
        }
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isOpen, updatePosition]);

    const toggleDropdown = () => {
        if (disabled) return;
        if (!isOpen) {
            calculateDropdownPosition();
            setSearchTerm('');
        }
        setIsOpen(!isOpen);
    };

    const handleSelect = (item: DropdownItem) => {
        onChange(item);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {label && (
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-700 dark:text-gray-200 mb-1">
                    <Icon size={15} className='text-orange-500' />
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}

            <button
                ref={buttonRef}
                type="button"
                onClick={toggleDropdown}
                disabled={disabled}
                className={`w-full flex items-center justify-between bg-white dark:bg-slate-900 border ${error ? 'border-red-500' : 'border-gray-300 dark:border-gray-700'} rounded-lg px-3 py-3 text-[13px] text-gray-900 dark:text-white focus:border-orange-500 focus:bg-gray-50 dark:focus:bg-gray-800 outline-none transition-all shadow-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400 dark:hover:border-gray-600'} ${buttonClassName}`}
            >

                <div className="flex items-center gap-2 overflow-hidden min-w-0 flex-1">
                    {value ? (
                        <div className="flex flex-col items-start min-w-0 text-left">
                            <span className="truncate text-gray-900 dark:text-white text-left w-full font-medium">
                                {getDisplayText ? getDisplayText(value) : value[displayProperty]}
                            </span>
                            {(getSecondaryDisplayText || (secondaryDisplayProperty && value[secondaryDisplayProperty])) && (
                                <span className="truncate text-[10px] text-gray-500 dark:text-gray-400 text-left w-full">
                                    {getSecondaryDisplayText ? getSecondaryDisplayText(value) : value[secondaryDisplayProperty as string]}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-400 dark:text-gray-500 truncate">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {value && !disabled && (
                        <X
                            onClick={handleClear}
                            className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        />
                    )}
                    <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-400" />
                </div>
            </button>

            {
                isOpen && createPortal(
                    <div
                        ref={menuRef}
                        className={`fixed z-[99999] bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${dropdownPosition === 'top' ? 'origin-bottom' : 'origin-top'}`}
                        style={{ ...menuStyle, maxHeight: '320px' }}
                    >
                        <div className="p-2 border-b border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-slate-900/50 sticky top-0 z-10 backdrop-blur-sm">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-md pl-8 pr-3 py-1.5 text-[12px] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500 outline-none transition-all"
                                    placeholder={searchInputPlaceholder}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto max-h-[260px] custom-scrollbar">
                            {isLoading && allItems.length === 0 ? (
                                <div className="p-4 text-center">
                                    <div className="w-5 h-5 border-2 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-2" />
                                    <span className="text-[11px] text-gray-500 font-medium tracking-wide">FETCHING DATA...</span>
                                </div>
                            ) : filteredItems.length > 0 ? (
                                <div className="p-1">
                                    {filteredItems.map((item) => {
                                        const isSelected = value && item[idProperty] === value[idProperty];
                                        return (
                                            <button
                                                key={item[idProperty] || Math.random()}
                                                type="button"
                                                onClick={() => handleSelect(item)}
                                                className={`w-full flex items-center justify-between px-2.5 py-2 rounded-md text-[13px] transition-all group ${isSelected ? 'bg-orange-500/10 dark:bg-orange-600/20 text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'}`}
                                            >
                                                <div className="flex flex-col items-start min-w-0 text-left w-full">
                                                    <span className="truncate w-full text-left">
                                                        {getDisplayText ? getDisplayText(item) : item[displayProperty]}
                                                    </span>
                                                    {(getSecondaryDisplayText || (secondaryDisplayProperty && item[secondaryDisplayProperty])) && (
                                                        <span className="text-[10px] text-gray-500 truncate w-full group-hover:text-gray-700 dark:group-hover:text-gray-400 text-left">
                                                            {getSecondaryDisplayText ? getSecondaryDisplayText(item) : item[secondaryDisplayProperty as string]}
                                                        </span>
                                                    )}
                                                </div>
                                                {isSelected && <Check className="w-4 h-4 shrink-0" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="p-6 text-center">
                                    <div className="p-3 bg-gray-100 dark:bg-slate-800/50 rounded-full w-fit mx-auto mb-3">
                                        <Search className="w-5 h-5 text-gray-400 dark:text-gray-600" />
                                    </div>
                                    <p className="text-[12px] text-gray-900 dark:text-white font-semibold">No results found</p>
                                    <p className="text-[11px] text-gray-500 mt-1">Try a different search term</p>
                                </div>
                            )}
                        </div>
                    </div>,
                    document.body
                )
            }

            {error && <p className="mt-1 text-[10px] text-red-500 font-medium">{error}</p>}
        </div >
    );
});

SingleSelectLocalSearchApiDropdown.displayName = 'SingleSelectLocalSearchApiDropdown';

export default SingleSelectLocalSearchApiDropdown;
